/**
 * @Description File consists all the common utility for the program
 * @FileName utility.js
 * @Author Arnab Sutar - sutaarn
 * @CreatedOn 11 January, 2021 04:21:20
 * @IssueID - 318, 305
 */
import { LogEventLevel } from 'structured-log';
import { useSelector } from 'react-redux';
import numeral from 'numeral';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import appHistory from './appHistory';
import { exportFileType, globalConstants, staticCommonLabelKeys } from '../moduleConstants';
import { setLookUpsData } from '../redux/actions/commonActions';
import {
  currencyFormat,
  formatWithSymbolNumber,
  padZeroAfterInterger,
} from './currencyFormatUtility';

const { dateFormatMMDDYYYY, dateFormatYYYYMMDD, dateFormatDDMMYYYHHMM } = globalConstants;

// Delay in milliseconds to wait for modal to fully close before scrolling
const MODAL_CLOSE_SCROLL_DELAY_MS = 350;

export * from './stringUtility';
export * from './errorHandlerUtility';
export * from './dataFormatUtility';
export * from './currencyFormatUtility';
/**
  * Register the localization option for number formatting.
  * Currently only english and french has been configured.
  * Make registration idempotent to avoid "locale already registered" during tests.
  */
try {
  const isFrRegistered = (numeral && numeral.locales && numeral.locales.fr);
  if (!isFrRegistered) {
    numeral.register('locale', 'fr', {
      delimiters: {
        thousands: ' ',
        decimal: ',',
      },
      abbreviations: {
        thousand: 'k',
        million: 'm',
        billion: 'b',
        trillion: 't',
      },
      // ordinal: (number) => (number === 1 ? 'er' : 'ème'),
      currency: {
        symbol: 'C$',
      },
    });
  }
} catch (e) {
  // Ignore duplicate registration errors in test environments
}

/**
 * Utility function to convert ButtonSelector value to boolean
 * After MUX update, ButtonSelector returns boolean instead of string
 * This handles both old string ('true'/'false') and new boolean (true/false) values
 * @param {boolean|string} val - The value from ButtonSelector
 * @returns {boolean} - The boolean value
 */
export const toBooleanValue = (val) => val === 'true' || val === true;

/**
 * Utility function to convert boolean to ButtonSelector format
 * Converts boolean to string format for ButtonSelector value prop
 * @param {boolean} val - The boolean value
 * @returns {string} - String 'true' or 'false'
 */
export const toButtonSelectorValue = (val) => (val ? 'true' : 'false');

const channelHierarchy = [
  'COMPANY',
  'CHANNEL',
  'REGION',
  'DISTRIBUTOR',
  'ALTERNATE OFFICE',
  'LOCATION',
  'TEMP LEVEL 7',
  'CORPORATION',
  'AFFILIATE',
  'ADVISOR',
];
const caseNoThree = 3;
const caseNoSeven = 7;
const caseNoFifteen = 15;
const caseNoThirtyOne = 31;
const caseNoSxityThreen = 63;
const caseNoOne = 1;
const caseNoTwo = 2;
const notificationTime = 100000;
const pageSizeTen = '10';
/**
  * Get the log level numeric value for a log level string
  * @param {string} levelStr
  *
  * @returns numeric value of the log level
  */
export const getLogLevel = (levelStr) => {
  switch (levelStr) {
    case 'off':
      return LogEventLevel.off;
    case 'fatal':
      return LogEventLevel.fatal;
    case 'error':
      return LogEventLevel.error;
    case 'warning':
      return LogEventLevel.warning;
    case 'information':
      return LogEventLevel.information;
    case 'debug':
      return LogEventLevel.debug;
    case 'verbose':
      return LogEventLevel.verbose;
    default:
      return LogEventLevel.verbose;
  }
};
/**
  *
  * @param {LogEvent from structured-log} e
  * @returns enrich the log event by adding following properties :
  * - message
  * - levelValue
  * - level (updated to winston log level value.)
  */
export const enrichLogEvent = (e) => {
  if (!e) {
    return e;
  }
  e.message = e.messageTemplate ? e.messageTemplate.render(e.properties) : null;
  e.levelValue = e.level;
  switch (e.level) {
    case caseNoOne:
      e.level = 'fatal';
      break;
    case caseNoThree:
      e.level = 'error';
      break;
    case caseNoSeven:
      e.level = 'warning';
      break;
    case caseNoFifteen:
      e.level = 'information';
      break;
    case caseNoThirtyOne:
      e.level = 'debug';
      break;
    case caseNoSxityThreen:
      e.level = 'verbose';
      break;
    default:
      e.level = null;
      break;
  }
  return e;
};
/**
  * Application environment config detail.
  */
export const environment = () => (window.SERVER_DATA ? window.SERVER_DATA : {});
const envData = environment();

/**
  * set the locale for number format
  * @param {string} locale - en or fr
  */
export const setNumberLocale = (localeLang) => numeral.locale(localeLang);
/**
  * Format the number value for the locale
  * @param {number} val - value which needs to be formatted
  * @param {string} formatStr - format string for number formatting
  */
export const formatNumber = (val, formatStr = null) => numeral(val).format(formatStr || null);
export const navigate = (path) => {
  appHistory.push(path);
  return true;
};

/**
  * Scroll the main application content area to the top.
  * MUX Layout sets `body` to `overflow-y: hidden` and uses
  * `.mainLayoutContainer` (LayoutContainer) as the scrollable region.
  * This utility ensures the visible content is scrolled to the top,
  * trying multiple approaches to ensure compatibility.
  */
export const scrollToTop = () => {
  const performScroll = () => {
    // Approach 1: Try scrolling the mainLayoutContainer
    const mainContainer = document.querySelector('.mainLayoutContainer');
    if (mainContainer) {
      mainContainer.scrollTop = 0;
      // Also use scrollTo for browsers that need it
      if (typeof mainContainer.scrollTo === 'function') {
        mainContainer.scrollTo(0, 0);
      }
    }

    // Approach 2: Try scrolling the fasatPage to top using scrollIntoView
    const pageElement = document.querySelector('.fasatPage');
    if (pageElement) {
      pageElement.scrollIntoView({ block: 'start', behavior: 'auto' });
    }

    // Approach 3: Document elements
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }

    // Approach 4: Window scroll
    window.scrollTo(0, 0);
  };

  // Wait for modal to fully close and DOM to update
  setTimeout(performScroll, MODAL_CLOSE_SCROLL_DELAY_MS);
};
/**
  *
  * @param {Array} e
  * @returns {Array} :
  * - sort in Alphabetic order
  */
export const alphabeticSort = (requestObj) => requestObj.sort((a, b) => {
  const textA = a.label.toUpperCase();
  const textB = b.label.toUpperCase();
  let flag = 0;
  if (textA < textB) {
    flag = -1;
  } else {
    flag = 1;
  }
  return flag;
});
/**
  *
  * @param {Array} e
  * @returns {Array} :
  * - sort in Channel Hierarchy
  */
export const sortChannelHierarchy = (requestObj) => requestObj.sort((a, b) => {
  const textA = a.label.toUpperCase();
  const textB = b.label.toUpperCase();
  let flag = 0;
  if (channelHierarchy.indexOf(textA) < channelHierarchy.indexOf(textB)) {
    flag = -1;
  } else {
    flag = 1;
  }
  return flag;
});

/**
  * return column asyn or desc as per sorted Object or send null
  * @param {object} sortingObject
  * @param {string} columnID
  * @returns
  */
export const setColumnSettings = (sortingObject, columnID) => {
  if (sortingObject) {
    const foundIndex = sortingObject.findIndex((x) => x.key === columnID);
    if (foundIndex >= 0) {
      return sortingObject[foundIndex].asc === true ? 1 : -1;
    }
  }
  return null;
};

/**
  * configure column config list for filter component
  * @param {array} tableMetaData
  * @returns
  */
export const getColumns = (tableMetaData) => tableMetaData.columnConfig
  .map(({ id, header, aclKey, type, lookupKey, isSearchable }) => ({
    label: header,
    value: id,
    aclKey: aclKey || null,
    type: type || null,
    lookupKey: lookupKey || null,
    isSearchable: isSearchable || null,
  }))
  .sort((a, b) => {
    if (a.label < b.label) {
      return -1;
    }
    if (a.label > b.label) {
      return 1;
    }
    return 0;
  })
  .filter(({ label }) => label !== 'Actions');

/**
  * Format Column Order Item For UI readability
  * @param {array} selectedItem
  * @returns
  */
export const formatColumnOrderForUI = (selectedItem) => selectedItem
  .sort((a, b) => (a.order > b.order ? 1 : -1)).map((item) => item.key);

export const formatRoleList = (roleList) => roleList.map(({ sgrName }) => ({
  value: sgrName,
  label: sgrName,
}));

export const formatColumnOrderAsAPI = (selectedItem) => selectedItem.map((item, index) => ({
  key: item,
  oredr: index + 1,
}));

const formatRecordKey = ({ parameters, lockKey }) => {
  let recordKey = '';
  if (lockKey) {
    recordKey = parameters.filter(({ key }) => key === lockKey)[0].values.join();
  } else if (parameters) {
    parameters.map((item) => {
      recordKey = recordKey ? `${recordKey}:${item.values.join(':')}` : item.values.join(':');
      return item;
    });
  } else {
    recordKey = '';
  }
  return recordKey;
};

const formatFilterCriteriaParams = (customFilterCreteriaList) => customFilterCreteriaList
  .map(({ key, operator, type, values }) => {
    const valueArray = typeof values === 'string' ? values.split(',') : values;
    return {
      key,
      operator,
      type,
      values: valueArray === null ? [] : valueArray,
    };
  });

/**
  * Initial request payload for List request
  * @param {string} locale
  * @param {object} parameters
  * @param {object} sortOptions
  * @param {object} groupByParameters
  * @param {object} lookUpInformation
  * @returns
  */
const setInitialVal = (value, defaultVal) => value || defaultVal;
export const setParam = (value, defaultVal) => {
  let response = defaultVal;
  if ((value && Array.isArray(value))) {
    response = formatFilterCriteriaParams(value).filter((item) => {
      const oprChck1 = item.operator === 'OPT_IS_NULL' || item.operator === 'OPT_IS_NOT_NULL';
      const oprChck2 = item.operator === 'OPT_IS_EMPTY' || item.operator === 'OPT_IS_NOT_EMPTY';
      if (oprChck1 || oprChck2) {
        return item;
      }
      return item.values[0];
    });
  }
  return response;
};
export const initialReqObject = ({
  locale,
  currentPage,
  parameters,
  sortOptions,
  groupByParameters,
  isDistinct,
  lookUpInformation,
  pageSize,
  entityId,
  lockType,
  loggedUser,
  lockKey,
  listColumnOrder,
  isExtSrceen,
}) => ({
  entityId: `${entityId}`,
  active: true,
  export: 'NONE',
  page: setInitialVal(currentPage, 1),
  pageSize: setInitialVal(pageSize, pageSizeTen),
  locale: locale === 'en' ? 'E' : 'F',
  lookUpInformation: setInitialVal(lookUpInformation, []),
  columnOrders: listColumnOrder || [],
  parameters: setParam(parameters, []),
  groupByParameters: setInitialVal(groupByParameters, []),
  isDistinct: setInitialVal(isDistinct, false),
  sortOptions: setInitialVal(sortOptions, []),
  ...(isExtSrceen
    && { userId: isExtSrceen?.userId }),
  ...(lockType && { lock: {
    entityKey: `${entityId}`,
    recordKey: parameters ? formatRecordKey({ parameters, lockKey }) : '',
    source: 'ONLINE',
    lockId: 0,
    lockedBy: loggedUser?.UserId,
    lockStartTime: moment().format(),
    sessionId: '',
    action: lockType,
  } }),
  ...(lockKey && { lockKey }),
});

export const initialReqObjectSelf = ({
  locale,
  currentPage,
  parameters,
  sortOptions,
  groupByParameters,
  isDistinct,
  lookUpInformation,
  pageSize,
  entityId,
  lockType,
  loggedUser,
  lockKey,
  listColumnOrder,
  isExtSrceen,
}) => ({
  entityId: `${entityId}`,
  active: true,
  export: 'NONE',
  page: setInitialVal(currentPage, 1),
  pageSize: setInitialVal(pageSize, pageSizeTen),
  locale,
  lookUpInformation: setInitialVal(lookUpInformation, []),
  columnOrders: listColumnOrder || [],
  parameters: [],
  groupByParameters: setInitialVal(groupByParameters, []),
  // isDistinct: setInitialVal(isDistinct, false),
  // sortOptions: setInitialVal(sortOptions, []),
  isDistinct: setInitialVal(isDistinct, false),
  sortOptions: setInitialVal(sortOptions, []),
  ...(isExtSrceen
    && { userId: isExtSrceen?.userId }),
  ...(lockType && { lock: {
    entityKey: `${entityId}`,
    recordKey: parameters ? formatRecordKey({ parameters, lockKey }) : '',
    source: 'ONLINE',
    lockId: 0,
    lockedBy: loggedUser?.UserId,
    lockStartTime: moment().format(),
    sessionId: '',
    action: lockType,
  } }),
  ...(lockKey && { lockKey }),
});

export const initReqObjBlankVal = ({
  locale,
  currentPage,
  parameters,
  sortOptions,
  groupByParameters,
  isDistinct,
  lookUpInformation,
  pageSize,
  entityId,
  lockType,
  loggedUser,
  lockKey,
  listColumnOrder,
}) => ({
  entityId: `${entityId}`,
  active: true,
  export: 'NONE',
  page: setInitialVal(currentPage, 1),
  pageSize: setInitialVal(pageSize, pageSizeTen),
  locale: locale === 'en' ? 'E' : 'F',
  lookUpInformation: setInitialVal(lookUpInformation, []),
  columnOrders: listColumnOrder || [],
  parameters: setInitialVal(parameters, []),
  groupByParameters: setInitialVal(groupByParameters, []),
  isDistinct: setInitialVal(isDistinct, false),
  sortOptions: setInitialVal(sortOptions, []),
  ...(lockType && { lock: {
    entityKey: `${entityId}`,
    recordKey: parameters ? formatRecordKey({ parameters, lockKey }) : '',
    source: 'ONLINE',
    lockId: 0,
    lockedBy: loggedUser?.UserId,
    lockStartTime: moment().format(),
    sessionId: '',
    action: lockType,
  } }),
  ...(lockKey && { lockKey }),
});
/**
  * Reset Upsert Object
  */
export const resetUpsertValue = {
  upsertRequest: null,
  upsertRequestError: null,
  upsertRequestedOn: null,
  upsertRequestProcessedOn: null,
  upsertData: null,
};
/**
  * Column Type Datatype
  */
export const COLUMN_TYPE = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
  BOOLEAN: 'boolean',
  ACTION: 'action',
};
/**
  * Reset Delete Object
  */
export const resetDeletetValue = {
  deleteRequest: null,
  deleteRequestError: null,
  deleteRequestedOn: null,
  deleteRequestProcessedOn: null,
  deleteData: null,
};
/**
  * Replace occurence of characters in a strings
  * @param {string} strS
  * @param {string} replaceWith
  * @param {string} replace
  * @returns string
  */
export const replaceString = (str, replaceWith, replace) => str.toString().replace(replace, replaceWith);

export const negetiveValueFormat = (num, locale, decimalPlaces) => {
  let formatedValue = null;
  const amtvalue = num * 1;
  const amountValue = amtvalue < 0 ? amtvalue * -1 : amtvalue;
  if (decimalPlaces > 0) {
    formatedValue = currencyFormat(amountValue, locale, decimalPlaces);
  } else {
    formatedValue = amountValue;
  }
  return formatWithSymbolNumber(num, formatedValue);
};

export const currencyExchange = (num, locale, decimalPlaces, withSymbol) => {
  let lang;
  let currencyType;
  const currencySymbol = '$';
  let decimalSymbol;
  if (locale === 'en') {
    lang = 'en-US';
    currencyType = 'USD';
    decimalSymbol = '.';
  } else {
    lang = 'fr-FR';
    currencyType = 'EUR';
    decimalSymbol = ',';
  }
  const formatter = new Intl.NumberFormat(lang, {
    style: 'currency',
    currency: currencyType,
    minimumFractionDigits: decimalPlaces,
  });
  let data;
  const spChars = /,+/;
  if (spChars.test(num)) {
    const splitString = num.replace(/\s/g, '').split(',');
    data = formatter.format(splitString[0]);
    const numZeroPad = padZeroAfterInterger(splitString[1], decimalPlaces);
    const result = data.substring(0, data.lastIndexOf(decimalSymbol) + 1);
    data = result.concat('', numZeroPad);
  } else {
    data = formatter.format(num);
  }
  const newData = data.slice(0, (data.length - 1));
  if (withSymbol) {
    return newData.replace('€', '$');
  }
  /* Symbol remover $, € */
  return newData.replace(currencySymbol, '');
};
/**
  * Currency Format
  * @param {number} value
  * @param {string} locale
  * @returns string
  */
export const currencyLabelFormat = (num, locale, decimalPlaces) => {
  if (locale === 'en') {
    return `$${currencyFormat(num, locale, decimalPlaces)}`;
  }
  return `${currencyFormat(num, locale, decimalPlaces)}$`;
};
/**
  * Currency Format To Decimal
  * @param {string} value
  * @param {string} locale
  * @returns string
  */
export const currencyFormatToDecimal = (value, locale) => {
  let splitedValue;
  if (locale === 'en' && value) {
    splitedValue = value.toString().split(',').join('');
  } else if (locale === 'fr' && value && value.toString().includes(',')) {
    splitedValue = value.split(',').join('.').split(/\s/g).join('');
  } else {
    splitedValue = value;
  }
  return splitedValue;
};
/**
  * Append zero before digit
  * @param {object} responseData
  * @returns
  */
export const padWithZeroes = (number, len) => {
  const zeroes = '0'.repeat(len);
  return zeroes.substring(number.toString().length, len) + number;
};
/**
  * render download file name
  * @param {object} responseData
  * @returns
  */
export const renderFileName = (responseData) => responseData.headers['content-disposition']
  .split(';')
  .find((n) => n.includes('filename='))
  .replace('filename=', '')
  .trim();
const checkSubMenu = ({ menuItem, userClaim }) => {
  let result = true;
  if (menuItem.aclKey && menuItem.aclKey.length > 0) {
    const aclObject = userClaim?.access.find(
      (i) => menuItem.aclKey.findIndex((ii) => ii === i.resource) > -1,
    );
    if (aclObject && (aclObject.permission !== 'ENABLED')) {
      result = false;
    }
    if (!aclObject) {
      result = false;
    }
  }
  return result;
};
/**
  * function to generate AC
  * @param {object} userClaim - RBAC data for user
  * @param {object} menuItem - Child menu Node
  */
export const IsACLEnabled = (userClaim, menuItem, loggedUser) => {
  let result = true;
  result = checkSubMenu({ menuItem, userClaim });
  if (result && (menuItem.subMenu && menuItem.subMenu.length > 0)) {
    // eslint-disable-next-line no-param-reassign
    menuItem.subMenu = menuItem.subMenu.filter((i) => IsACLEnabled(userClaim, i, loggedUser));
  }
  return result;
};
/**
  * function to generate AC
  * @param {object} userClaim - RBAC data for user
  * @param {object} menuItem - Child menu Node
  */
export const formatQuickMenus = (userClaim, menuItems, userType) => {
  const hasSubMenu = userClaim.subMenu && userClaim.subMenu.length > 0;
  if (userType === 'external') {
    if (hasSubMenu && userClaim.external) {
      userClaim.subMenu.map((i) => formatQuickMenus(i, menuItems, userType));
    }
    if (userClaim.external) {
      menuItems.push({ id: userClaim.id, label: userClaim.label });
    }
  }
  if (userType === 'internal') {
    if (hasSubMenu && !userClaim.external) {
      userClaim.subMenu.map((i) => formatQuickMenus(i, menuItems, userType));
    }
    if (!userClaim.external) {
      menuItems.push({ id: userClaim.id, label: userClaim.label });
    }
  }
  return menuItems;
};
export const formatMenuFunc = (uniqueItems, item) => {
  if (uniqueItems.length > 0) {
    if (uniqueItems.filter((idx) => idx.menuId === item.menuId).length > 0) {
      return true;
    }
    return uniqueItems.push(item);
  }
  return uniqueItems.push(item);
};
export const hasUserClaim = (aclkey) => {
  const userClaim = useSelector(({ ApplicationStateRBAC }) => ApplicationStateRBAC.rbacData);
  if (aclkey) {
    const aclObject = userClaim?.access.find((i) => i.resource === aclkey);
    if (aclObject) {
      return aclObject.permission === 'ENABLED';
    }
    return false;
  }
  return true;
};
export const checkForResourceAccess = (userClaim, resourceId) => {
  if (!resourceId) { // If it is the root level resource
    return true;
  }
  const aclObject = userClaim?.access.find((i) => i.resourceId === resourceId);
  if (aclObject) {
    return aclObject.parentResourceId === null || checkForResourceAccess(userClaim, aclObject.parentResourceId);
  }
  return false;
};
export const isHasUserClaim = (userClaim, aclkey) => {
  if (aclkey) {
    const aclObject = userClaim?.access.find((i) => i.resource === aclkey);
    if (aclObject) {
      return aclObject.permission === 'ENABLED' && checkForResourceAccess(userClaim, aclObject.parentResourceId);
    }
    return false;
  }
  return false;
};
export const makeNumber = () => {
  const targetLength = 4;
  const beginIndex = 3;
  const endIndex = 13;
  let seed = Date.now();
  const result = (seed).toString().padStart(targetLength, '0').slice(beginIndex, endIndex);
  seed = parseInt(result, 10);
  return seed;
};
export const getUniqueId = () => Date.now() + (makeNumber() * notificationTime).toFixed();
/**
  * reset request object for list when page page size change
  * @param {object, string}
  * @returns
  */
export const pageChngRequestObj = ({ reqObj, currentPageSize }) => {
  const reqObject = reqObj;
  reqObject.pageSize = currentPageSize;
  reqObject.page = 1;
  return reqObject;
};
export const getLabelErr = (userPrefError, t, currentYear) => {
  if (userPrefError) {
    return t(staticCommonLabelKeys.LS_FOOTER_TEXT_ERROR, { currentYear });
  }
  return t('LS_FOOTER_TEXT', { currentYear });
};
export function FasatCopyRightMessage() {
  const [t] = useTranslation();
  const userPrefError = useSelector(({ ApplicationStateRBAC }) => ApplicationStateRBAC.userPrefError);
  const currentYear = new Date().getFullYear();
  return (
    <>{getLabelErr(userPrefError, t, currentYear)}</>
  );
}
export const setConSysLangParams = ({ storeSetState, locale }) => {
  let lang = null;
  if (storeSetState) {
    lang = locale === 'en' ? 'E/true' : 'F/true';
  } else {
    lang = locale === 'en' ? 'E/false' : 'F/false';
  }
  return lang;
};
export const temAdjApiFormat = (rowSelected, userId, isApprove) => {
  let selItem = '';
  rowSelected.map((item) => {
    selItem = `${selItem}${item.id},`;
    return item;
  });
  let responseUserId = { userId };
  if (isApprove) {
    responseUserId = { wadApproveUser: userId };
  }
  return { selectedIds: selItem.substring(0, selItem.length - 1), ...responseUserId };
};

export const getErrsDynFld = (fieldErr, formikData, _beErrors, _rules, _feRuleConfig, isBorder, tran) => {
  let controlErrors = [];
  if (fieldErr) {
    controlErrors = [
      {
        id: formikData.errors[fieldErr],
        msg: !isBorder && tran(fieldErr),
        isError: true,
      },
    ];
  }
  return [...controlErrors];
};
const THREE = 3;
const TWO = 2;
const NINENINENINE = 999;
export const dateValidator = (value) => {
  if (value) {
    const dateArr = value.split('/');
    if (dateArr.length !== THREE || !dateArr[TWO] || dateArr[TWO] <= NINENINENINE
       || !moment(value, dateFormatMMDDYYYY).isValid()) {
      return false;
    }
    const dateIsBefore = moment(value, dateFormatMMDDYYYY)
      .isBefore(moment(globalConstants.minAllowedDate, dateFormatMMDDYYYY));
    if (dateIsBefore) {
      return false;
    }
  }
  return true;
};

export const dateFieldValidator = (value) => {
  if (value) {
    const dateArr = value.split('/');
    if (dateArr.length !== THREE || !dateArr[TWO] || dateArr[TWO] <= NINENINENINE
       || !moment(value, dateFormatMMDDYYYY).isValid()) {
      return false;
    }
  } return true;
};
export const isCheckBoxTrue = (val) => {
  if (val) { return 'checked'; }
  return '';
};
export const padTime = (time) => (String(time).length === 1 ? `0${time}` : `${time}`);
export const getDateAPIFormat = (value) => {
  if (value) {
    const trimedDateStr = String(value).trim();
    return moment(trimedDateStr).format(dateFormatYYYYMMDD);
  }
  return null;
};
export const getCurrentDate = () => moment().format();
export const getCurrentDateFormat = (format) => moment().format(format);
export const getAddDaysToDate = (date, days) => moment(date).add(Number(days), 'days');
export const getSubtractDaysToDate = (date, days) => moment(date).subtract(Number(days), 'days');
export const getDateFormatYYMMDD = (date) => moment(date).format(dateFormatYYYYMMDD);
export const getDateFormatDDMMYYHHMM = (date) => moment(date).format(dateFormatDDMMYYYHHMM);
export const formatResponse = ({ action, responseData, listToDetail = false }) => {
  const { parameters, entityId, lockKey, lock } = action.data;
  let lockedBy = null;
  if (lock) {
    lockedBy = lock.lockedBy;
  }
  const comboLock = {
    lockedBy,
    entityKey: entityId,
    recordKey: formatRecordKey({ parameters, lockKey }),
    source: 'ONLINE',
    lockId: 0,
    lockStartTime: moment().format(),
    sessionId: '',
    action: globalConstants.lockEdit,
  };
  if (Array.isArray(responseData)) {
    return { data: responseData, comboLock, listToDetail };
  }
  return { ...responseData, comboLock, listToDetail };
};
/**
  * validate Numeric or Alpha Numeric
  * @param {boolean} condition
  * @param {string} value
  * @returns string
  */
export const formatNumAndAlphaNum = (condition, value) => {
  if (condition) {
    return value.replace(/[^0-9\\]+/g, '');
  }
  const hasNoSplChar = value.replace(/[^a-zA-Z0-9]/g, '');
  return hasNoSplChar.toUpperCase();
};
export const sumOfRecordCount = {
  columnType: 'text',
  dataField: 'sumRecordCount',
  disableFilter: false,
  disableSortBy: false,
  header: 'Sum of records count',
  id: 'sumRecordCount',
  isAction: false,
  visibility: 'sumRecordCount',
  width: 160,
  sortAscending: 'sumRecordCount',
  aclKey: 'aclKey',
  type: 'number',
  lookupKey: '',
};
export const exportErrorMsg = (errorCode, fileType = exportFileType.Excel) => {
  if (errorCode === globalConstants.errorCode402) {
    return [{
      errorCode: 'COMMON_EXCEPTION_EXPORT_MAX_LIMIT',
      errorMessage: 'The number of records has exceeded the maximum limit.',
      errorMessageId: 'COMMON_EXCEPTION_EXPORT_MAX_LIMIT',
      type: 'RULE',
    }];
  }
  // Added check for CSV msg for row limit
  // By default it will treat as excel file
  const exportErrorCode = fileType === exportFileType.CSV ? 'ERR_9972' : 'ERR_9942';
  return [{ errorCode: exportErrorCode, errorMessage: exportErrorCode, errorMessageId: exportErrorCode, type: 'RULE' }];
};
const getChildren = (data) => {
  if (data && data[0] && data[0].children && data[0].children.length > 0) {
    return data[0].children;
  }
  return [];
};
export const updateLookupDataCOMCHAFEG = (lookupDataObject) => {
  if (lookupDataObject && lookupDataObject.length > 0) {
    const IDItems = lookupDataObject.filter((lookUpItem) => lookUpItem.key === 'ID_LEVEL');
    if (IDItems && IDItems.length > 0) {
      const dataObject = IDItems[0].values;
      const channel = dataObject.filter((lookUpItem) => lookUpItem.lookUpDbType === 'CHA');
      const company = dataObject.filter((lookUpItem) => lookUpItem.lookUpDbType === 'COM');
      const region = dataObject.filter((lookUpItem) => lookUpItem.lookUpDbType === 'REG');
      lookupDataObject.push({ key: 'CHANNEL', values: getChildren(channel) });
      lookupDataObject.push({ key: 'COMPANY', values: getChildren(company) });
      lookupDataObject.push({ key: 'REGION', values: getChildren(region) });
      setLookUpsData(lookupDataObject);
    }
  }
};
export const notEmpty = (value) => !(value === undefined || value === null || value === '');
export const autoSelFirstRow = (selClass) => {
  const gridTable = document.getElementsByClassName(`${selClass}`)[0];
  if (gridTable) {
    const trElems = document.querySelectorAll(`.${selClass} tr`);
    trElems.forEach((row) => row.classList.remove('highlightedRow'));
    const element = document.getElementsByTagName('tbody')[0].rows[0];
    element.classList.add('highlightedRow');
  }
};

export const getCurrentLocaleFormListData = (listData) => ((listData.locale === 'en'
|| listData.locale === 'E') ? 'en' : 'fr');
export const setConLangParams = ({ locale }) => ((locale === 'en') ? 'E' : 'F');
// #region utility functions for parsing FPE errors in Payments and adjustments
export const formatJsonStringForFinProcErr = (s) => s.replace(/\s/g, '').replaceAll(',}', '}').replaceAll(',]', ']');
export const parseJSONWithError = (str, defaultValue) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return defaultValue;
  }
};
export const getFormattedErrorForFinProc = (errCd, defaultErrMsg = 'static:GENERIC.NOTIFICATION') => {
  const errors = [];
  errCd.forEach((e) => {
    errors.push({ errorMessageId: e.PostStepErrorCode });
  });
  if (errors.length === 0) {
    errors.push({ errorMessageId: defaultErrMsg });
  }
  return errors;
};
export const downloadErrorMessage = (errorCode) => {
  if (errorCode === globalConstants.errorCode402) {
    return [{
      errorCode: 'FORM_LETTER_NOT_FOUND',
      errorMessage: 'Letter Not Found .',
      errorMessageId: 'FORM_LETTER_NOT_FOUND',
      type: 'RULE',
    }];
  }
  return [{
    errorCode: 'ERR_9942',
    errorMessage: 'ERR_9942',
    errorMessageId: 'ERR_9942',
    type: 'RULE',
  }];
};
// #endregion
export const addZerosToNumber = (num, places) => parseFloat(num).toFixed(places);
// eslint-disable-next-line no-restricted-globals
export const numberIsNanCheck = (value) => typeof value === 'number' && !isNaN(value);

export const getDigitIntegerCheckFunc = (value) => Math.floor(value) === value && Math.ceil(value) === value;
// object values replacement
export const objectConvert = (values) => {
  const arrayData = [];
  let key;
  // eslint-disable-next-line no-restricted-syntax
  for (key in values) {
    if (Object.prototype.hasOwnProperty.call(values, key)) {
      arrayData.push(values[key]);
    }
  }
  return arrayData;
};

export const objectEntriesReplace = (values) => {
  const returnArr = [];
  for (const key in values) {
    if (Object.prototype.hasOwnProperty.call(values, key)) {
      returnArr.push([key, values[key]]);
    }
  }
  return returnArr;
};

export const getCheckBoxParams = (formField, value) => ({
  key: `${formField}-key-${value}`,
  id: `${formField}-id-${value}`,
  defaultChecked: value,
});

export const getCurYrYtdSlctrLabel = (t, selectionOption) => `${t(staticCommonLabelKeys.COMMON_LABEL_CURR_YEAR_YTD)} ${t(selectionOption === '0' ? staticCommonLabelKeys.COMMON_LABEL_PREV_YEAR_YTD : staticCommonLabelKeys.COMMON_LABEL_PREV_YEAR_MONTH)}`;

export const sanitizeObject = (obj) => {
  const clean = {};
  for (const [key, value] of Object.entries(obj || {})) {
    if (typeof value === 'string') {
      clean[key] = value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;');
    } else {
      clean[key] = value;
    }
  }
  return clean;
};
